import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type PublicEvent } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const monthShort = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

function fmtTime(d: Date): string {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function fmtWeekday(d: Date): string {
  return d.toLocaleDateString("en-US", { weekday: "long" });
}

function fmtYear(d: Date): string {
  return d.getFullYear().toString();
}

function durationLabel(min: number | null): string {
  if (!min) return "";
  if (min % 60 === 0) {
    const h = min / 60;
    return `${h} hr${h > 1 ? "s" : ""}`;
  }
  return `${min} min`;
}

function groupByMonth(events: PublicEvent[]): Array<[string, PublicEvent[]]> {
  const groups = new Map<string, PublicEvent[]>();
  for (const e of events) {
    const d = new Date(e.scheduled_date);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const arr = groups.get(key) ?? [];
    arr.push(e);
    groups.set(key, arr);
  }
  return [...groups.entries()].map(([k, list]) => {
    const [y, m] = k.split("-").map(Number);
    const label = new Date(y, m, 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    return [label, list] as [string, PublicEvent[]];
  });
}

export default function Events() {
  useSeo({
    title: "Upcoming events · Henry Miller Museum",
    description:
      "Scheduled tours, programs, and gatherings at the Henry Miller Museum in St. Louis. Curated by museum staff.",
  });
  const [events, setEvents] = useState<PublicEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listEvents()
      .then((r) => setEvents(r.events))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"));
  }, []);

  return (
    <>
      {/* ── Hero ── */}
      <section className="border-b border-museum-line bg-museum-paper">
        <div className="max-w-5xl mx-auto px-5 py-12">
          <div className="text-[11px] uppercase tracking-eyebrow text-museum-accent2 font-semibold mb-3">
            Upcoming · Curated by museum staff
          </div>
          <h1 className="font-serif text-[40px] sm:text-[52px] leading-[1.05] text-museum-ink">
            What's on at the museum
          </h1>
          <div className="rule-gold mt-5" />
          <p className="mt-4 text-museum-muted max-w-2xl leading-relaxed">
            Scheduled tours, programs, and gatherings hosted at 2728 Martin Luther
            King Drive in St. Louis. The museum opens by appointment. To plan a
            visit for your group, use the request form.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/" className="btn-primary">Request a visit</Link>
            <a
              href="https://ibew.org/our-history-museum/henry-miller-museum/"
              target="_blank" rel="noopener noreferrer"
              className="btn-secondary"
            >
              About the museum
            </a>
          </div>
        </div>
      </section>

      {/* ── Body ── */}
      <section className="max-w-5xl mx-auto px-5 py-12">
        {error && (
          <div className="card p-6 text-museum-accent">{error}</div>
        )}

        {!error && events === null && (
          <div className="text-museum-muted">Loading…</div>
        )}

        {!error && events && events.length === 0 && <EmptyState />}

        {!error && events && events.length > 0 && (
          <div className="space-y-12">
            {groupByMonth(events).map(([month, list]) => (
              <div key={month}>
                <div className="flex items-center gap-4 mb-5">
                  <h2 className="font-serif text-2xl text-museum-ink">{month}</h2>
                  <div className="h-px flex-1 bg-museum-rule/40" />
                  <span className="text-xs uppercase tracking-eyebrow text-museum-muted">
                    {list.length} {list.length === 1 ? "event" : "events"}
                  </span>
                </div>
                <ul className="space-y-4">
                  {list.map((e) => (
                    <EventCard key={e.id} ev={e} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function EventCard({ ev }: { ev: PublicEvent }) {
  const d = new Date(ev.scheduled_date);
  const month = monthShort[d.getMonth()];
  const day = d.getDate();
  return (
    <li className="card overflow-hidden">
      <div className="grid sm:grid-cols-[140px_1fr] gap-0">
        {/* Date block */}
        <div className="bg-museum-accent text-museum-paper text-center py-6 px-4 flex flex-col justify-center">
          <div className="text-[11px] uppercase tracking-eyebrow opacity-80">
            {month}
          </div>
          <div className="font-serif text-[56px] leading-none mt-1">{day}</div>
          <div className="text-[11px] uppercase tracking-eyebrow opacity-80 mt-1">
            {fmtYear(d)}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-7 flex flex-col gap-2">
          <div className="text-[11px] uppercase tracking-eyebrow text-museum-accent2 font-semibold">
            {fmtWeekday(d)} · {fmtTime(d)}
            {ev.scheduled_duration_min ? ` · ${durationLabel(ev.scheduled_duration_min)}` : ""}
          </div>
          <h3 className="font-serif text-xl text-museum-ink">{ev.title}</h3>
          {ev.description && (
            <p className="text-sm text-museum-muted leading-relaxed whitespace-pre-line">
              {ev.description}
            </p>
          )}
          <div className="mt-2 text-xs text-museum-muted">
            Henry Miller Museum · 2728 Martin Luther King Dr., St. Louis
          </div>
        </div>
      </div>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="card p-10 text-center">
      <div className="section-eyebrow justify-center">Nothing scheduled yet</div>
      <h2 className="font-serif text-2xl mt-1">No public events at the moment</h2>
      <div className="rule-gold mx-auto" />
      <p className="text-museum-muted mt-4 max-w-prose mx-auto">
        Group visits to the Henry Miller Museum are always welcome by appointment.
        Submit a request and our volunteer staff will follow up to schedule
        something that fits your group.
      </p>
      <p className="mt-6">
        <Link to="/" className="btn-primary">Request a visit</Link>
      </p>
    </div>
  );
}
